import {useState} from "react";
import {FiExternalLink} from "react-icons/fi";
import {RiLightbulbLine} from "react-icons/ri";

import {ActionBarWrapper} from "@/components/actionBarWrapper/ActionBarWrapper.tsx";
import Button from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {CardStack} from "@/components/CardStack/CardStack.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {PopularIntegration} from "@/lib/queries/integrations";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";
import {IntegrationCard} from "@/pages/integrations/list/integrationsLibrary/IntegrationCard.tsx";

interface IntegrationsLibraryProps {
  activeModules: IntegrationModules[];
  hiddenModules?: Set<IntegrationModules>;
  setNewIntegration?: (newIntegration: IntegrationFormData) => void;
  popularIntegrations: PopularIntegration[];
}

export const IntegrationsLibrary = ({
  activeModules,
  hiddenModules,
  setNewIntegration,
  popularIntegrations,
}: IntegrationsLibraryProps) => {
  const [search, setSearch] = useState("");

  const popularModules =
    popularIntegrations.reduce<IntegrationModules[]>((acc, integration) => {
      const module = integration.module as IntegrationModules;
      if (IntegrationModuleInfo[module] && !hiddenModules?.has(module)) {
        acc.push(module);
      }
      return acc;
    }, []) ?? [];

  const allModules = (Object.keys(IntegrationModuleInfo) as IntegrationModules[]).filter(
    (module) =>
      !hiddenModules?.has(module) &&
      (IntegrationModuleInfo[module].label.toLowerCase().includes(search.toLowerCase()) ||
        (IntegrationModuleInfo[module].description ?? "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      <ActionBarWrapper
        className={"pb-4"}
        left={<SearchInput value={search} onChange={setSearch} variant={"tableControlBar"} />}
        right={
          <Button
            variant={"secondary"}
            className={"text-nowrap"}
            as={"a"}
            href={"https://support.uptime.com/hc/en-us/articles/115002534509-Overview-of-Push-Notifications"}
            target={"_blank"}
          >
            Support Docs <Icon icon={FiExternalLink} />
          </Button>
        }
      />

      {!search && (
        <>
          <div className="pulseTextH2Bold pb-4">Popular</div>
          <CardStack columns={{sm: 1, md: 1, lg: 1, xl: 1, xxl: 2}} className={"pb-6"}>
            {popularModules.map((module, index) => (
              <IntegrationCard
                key={index}
                module={module}
                isActive={activeModules.includes(module)}
                setNewIntegration={setNewIntegration}
              />
            ))}
          </CardStack>

          <div className="pulseTextH2Bold pb-4">All Integrations</div>
        </>
      )}

      {allModules.length === 0 && (
        <Card variant={"white"} className={"d-flex justify-content-between py-5 ps-4 pe-3"}>
          <div className={"d-flex flex-row align-items-center gap-3"}>
            <RiLightbulbLine className="pulse-color-gray-500" size="36" />
            <div>
              <div className={"pulseTextH3Bold"}>Don’t see your integration listed?</div>
              <div>Let us know which service you would like us to add next.</div>
            </div>
          </div>
          <Button variant={"secondary"} size={"small"} as={"a"} href={"https://feedback.uptime.com"} target={"_blank"}>
            Request Integration <Icon icon={FiExternalLink} />
          </Button>
        </Card>
      )}

      {allModules.length > 0 && (
        <CardStack columns={{sm: 1, md: 1, lg: 1, xl: 1, xxl: 2}}>
          {allModules.map((module, index) => (
            <IntegrationCard
              key={index}
              module={module}
              isActive={activeModules.includes(module)}
              setNewIntegration={setNewIntegration}
            />
          ))}
        </CardStack>
      )}

      <div className={"pulseTextBodySmall pt-4"}>
        {allModules.length} Total {allModules.length == 1 ? "Entry" : "Entries"}
      </div>
    </>
  );
};
